import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import StorefrontHeader from "../components/StorefrontHeader";
import Footer from "../components/Footer";
import ProductCard from "../components/ProductCard";
import { PLATFORMS } from "../lib/platforms";
import { Search } from "lucide-react";

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const platform = params.get("platform") || "";
  const category = params.get("category") || "";
  const search = params.get("search") || "";
  const featured = params.get("featured") === "true";

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState(search);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data.categories || []));
  }, []);

  useEffect(() => {
    setLoading(true);
    const q = {};
    if (platform) q.platform = platform;
    if (category) q.category = category;
    if (search) q.search = search;
    if (featured) q.featured = true;
    api.get("/products", { params: q }).then((r) => setProducts(r.data)).finally(() => setLoading(false));
  }, [platform, category, search, featured]);

  const setFilter = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    setParams(next);
  };

  const submitSearch = (e) => {
    e.preventDefault();
    setFilter("search", searchInput);
  };

  const activeChips = useMemo(() => {
    const out = [];
    if (platform) out.push({ k: "platform", label: PLATFORMS.find(p => p.key === platform)?.label });
    if (category) out.push({ k: "category", label: category });
    if (search) out.push({ k: "search", label: `“${search}”` });
    if (featured) out.push({ k: "featured", label: "Featured" });
    return out;
  }, [platform, category, search, featured]);

  return (
    <div data-testid="shop-page" className="min-h-screen bg-[#F8F8F7] text-[#0A0A0A]">
      <StorefrontHeader />

      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-8 pb-12">
        <p className="overline text-[#666]">The Catalog</p>
        <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter mt-4">
          Shop the edit.
        </h1>

        {/* Filters */}
        <div className="mt-10 grid lg:grid-cols-12 gap-6 items-end border-y border-black/10 py-6">
          <form onSubmit={submitSearch} className="lg:col-span-4 flex items-center gap-2 border border-black/15 px-3">
            <Search className="w-4 h-4 text-[#666]" />
            <input
              data-testid="search-input"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search products"
              className="bg-transparent w-full py-3 outline-none text-sm"
            />
          </form>

          <div className="lg:col-span-5 flex flex-wrap gap-2">
            <Chip active={!platform} onClick={() => setFilter("platform", "")} testid="filter-all">All Platforms</Chip>
            {PLATFORMS.map((p) => (
              <Chip key={p.key} active={platform === p.key} onClick={() => setFilter("platform", p.key)} testid={`filter-${p.key}`}>
                {p.label}
              </Chip>
            ))}
          </div>

          <div className="lg:col-span-3">
            <label className="overline text-[#666] block mb-2">Category</label>
            <select
              data-testid="category-filter"
              value={category}
              onChange={(e) => setFilter("category", e.target.value)}
              className="w-full bg-transparent border border-black/15 px-3 py-2.5 text-sm outline-none"
            >
              <option value="">All categories</option>
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>

        {/* Active chips */}
        {activeChips.length > 0 && (
          <div className="mt-5 flex flex-wrap gap-2 items-center">
            <span className="overline text-[#666]">Active</span>
            {activeChips.map((c) => (
              <button
                key={c.k}
                onClick={() => setFilter(c.k, "")}
                className="px-3 py-1 bg-[#0A0A0A] text-white text-xs uppercase tracking-wider"
              >
                {c.label} ×
              </button>
            ))}
          </div>
        )}

        {/* Grid */}
        <div className="mt-12">
          {loading ? (
            <div className="grid md:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => <div key={i} className="aspect-[4/5] bg-[#EBEBEA] animate-pulse" />)}
            </div>
          ) : products.length === 0 ? (
            <div className="border border-dashed border-black/20 p-16 text-center" data-testid="shop-empty">
              <h3 className="font-display font-black text-3xl tracking-tighter">No matches.</h3>
              <p className="text-sm text-[#666] mt-2">Try clearing some filters.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-6 lg:gap-8" data-testid="products-grid">
              {products.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}

function Chip({ active, onClick, children, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`px-4 py-2 text-xs uppercase tracking-wider font-semibold transition-colors ${
        active ? "bg-[#E53935] text-white" : "bg-transparent border border-black/15 hover:bg-[#0A0A0A] hover:text-white"
      }`}
    >
      {children}
    </button>
  );
}
